import { createComponent, createHook } from 'reakit-system';
import { Search } from '@mos-cat/ds-icons';
import { VisuallyHidden } from 'reakit';
import type { ComponentPropsWithoutRef } from 'react';
import { useContext, useMemo } from 'react';
import type { SystemStyleObject } from '@styled-system/css';
import { searchContext } from './internal/context';
import { createLanguageGetter } from './internal/text';
import type { BoxOptions } from '../Box/Box';
import { Box, useBox } from '../Box/Box';
import { kind } from '../Button/variants';
import type { StylesOptions } from '../hooks';
import { useFocusable } from '../hooks';

type SearchButtonHTMLProps = Omit<
  ComponentPropsWithoutRef<'button'>,
  'children'
>;

const useSearchButton = createHook<
  BoxOptions & StylesOptions,
  SearchButtonHTMLProps
>({
  compose: [useBox, useFocusable],
  useOptions(options) {
    return {
      ...options,
      borderRadius: 'button',
      border: 1,
      position: 'relative',
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      pt: 'btn48PaddingTop',
      px: 'btn48PaddingTop',
      pb: 'btn48PaddingBottom',
      styles: {
        ...options.styles,
        ...kind({ ...options, kind: 'primary' }),
        fontSize: 'btn48FontSize',
        fontWeight: 'btn48FontWeight',
        lineHeight: 'btn48LineHeight',
        '& svg': {
          width: 'btn48IconSize',
          height: 'btn48IconSize',
        },
      } as SystemStyleObject,
    };
  },
  useProps(_, props) {
    const { lang } = useContext(searchContext);
    const getText = useMemo(() => createLanguageGetter(lang), [lang]);
    return {
      ...props,
      children: (
        <>
          <VisuallyHidden>{getText('buttonLabel')}</VisuallyHidden>
          <Box as={Search} display={['inline-block', 'none']} />
          <Box aria-hidden="true" display={['none', 'inline-block']}>
            {getText('buttonLabel')}
          </Box>
        </>
      ),
    };
  },
});

export const SearchButton = createComponent({
  useHook: useSearchButton,
  as: 'button',
});
